import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import styles from './index.module.less';
import * as S from '@unionfab/ufc-shop-commons';
import {
  Upload,
  message as antMessage,
  Spin,
  Image,
  Input,
  Button,
  Modal,
} from 'antd';
import { UploadChangeParam } from 'antd/lib/upload';
import { LoadingOutlined } from '@ant-design/icons';
import SenderIcon from './sender-icon.svg';
import UserAvatar from './user-avatar.svg';
import SalesAvatar from './sale-avatar.svg';
import './antd-modal.less';

// 单例模式确保初始化调用只发生一次
const apiCallTracker = {
  hasInitialCallBeenMade: false,
};

interface MessagePageProps {
  code: string;
  userId: string;
  saleId: string;
  onMessagesChange: (count: number) => void;
}

const imgSuffix = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];

export const MessagePage: React.FC<MessagePageProps> = ({
  code,
  userId,
  saleId,
  onMessagesChange,
}) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<S.Message[]>([]);
  // const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [needScrollToBottom, setNeedScrollToBottom] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fileMapping, setFileMapping] = useState<Record<string, string>>({});
  const [canGetMoreMessages, setCanGetMoreMessages] = useState(true);

  // Refs
  const contentRef = useRef<HTMLDivElement>(null);
  const isFirstLoadRef = useRef(true);
  const messagesRef = useRef<S.Message[]>([]);
  const userIdRef = useRef(userId);
  const saleIdRef = useRef(saleId);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // 用于跟踪已处理的消息ID
  const processedMessageIds = useRef<Set<string>>(new Set());

  const { fileStoreAuth, loadFileStoreAuth } = S.useGlobalStore();

  useEffect(() => {
    userIdRef.current = userId;
    saleIdRef.current = saleId;
  }, [userId, saleId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    if (contentRef.current) {
      try {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;

        setTimeout(() => {
          if (contentRef.current) {
            contentRef.current.scrollTop = contentRef.current.scrollHeight;
          }
        }, 50);
      } catch (err) {
        console.error('Scroll error:', err);
      }
    }
  }, []);

  /**
   * 从可能包含文件大小的文件名中提取后缀
   * @param filename 文件名，如 "example.jpg" 或 "example.jpg(14.84 KB)"
   * @returns 文件后缀，如 "jpg"
   */
  function extractFileSuffix(filename: string): string {
    // 如果文件名包含大小信息 (xxx.jpg(14.84 KB))，先提取原始文件名部分
    const originalName = filename.includes('(')
      ? filename.substring(0, filename.lastIndexOf('('))
      : filename;

    // 从原始文件名中提取后缀
    return originalName.split('.').pop() || '';
  }

  /**
   * 从带有文件大小的文件名中分离出原始文件名和大小信息
   * @param fileNameWithSize 完整文件名，如 "example.pdf(14.84 KB)"
   * @returns 包含文件名和大小的对象，如 { name: "example.pdf", size: "14.84 KB" }
   */
  function separateFileNameAndSize(fileNameWithSize: string): {
    name: string;
    size: string;
  } {
    if (!fileNameWithSize.includes('(')) {
      return { name: fileNameWithSize, size: '' };
    }

    const lastOpenParenIndex = fileNameWithSize.lastIndexOf('(');
    const lastCloseParenIndex = fileNameWithSize.lastIndexOf(')');

    if (lastOpenParenIndex !== -1 && lastCloseParenIndex !== -1) {
      const fileName = fileNameWithSize.substring(0, lastOpenParenIndex);
      const fileSize = fileNameWithSize.substring(
        lastOpenParenIndex + 1,
        lastCloseParenIndex,
      );
      return { name: fileName, size: fileSize };
    }

    return { name: fileNameWithSize, size: '' };
  }

  const fetchMessages = useCallback(async () => {
    try {
      const currentUserId = userIdRef.current;
      if (!currentUserId) {
        console.log('Waiting for userId to be available...');
        return;
      }

      const params: S.OrderMessageListParams = {
        orderId: code,
      };

      const currentMessages = messagesRef.current;
      if (currentMessages.length > 0) {
        params.lastTimestamp =
          currentMessages[currentMessages.length - 1].timestamp;
      }

      const res = await S.queryOrderMessages(params);

      if (!res.data || res.data.length === 0) {
        if (isLoading) setIsLoading(false);
        return;
      }

      // 过滤出未处理过的新消息
      const newMessages = res.data.filter(
        msg => !processedMessageIds.current.has(msg.messageId),
      );

      if (newMessages.length === 0) {
        if (isLoading) setIsLoading(false);
        return;
      }

      // 处理新消息
      const processedMessages: S.Message[] = newMessages.map(msg => {
        // 将消息ID添加到已处理集合中
        processedMessageIds.current.add(msg.messageId);

        return {
          ...msg,
          displayTime: new Date(msg.timestamp).toLocaleString(),
          suffix:
            msg.messageType !== 'TEXT'
              ? extractFileSuffix(msg.message)
              : undefined,
        };
      });

      // 获取文件映射
      const fileIds = processedMessages
        .filter(msg => msg.messageType !== 'TEXT')
        .map(msg => msg.fileId);

      if (fileIds.length > 0) {
        const data = await S.getFilesByIds(fileIds);
        const newFileMapping = { ...fileMapping };

        Object.entries(data).forEach(([fileId, fileInfo]: [string, any]) => {
          if (fileInfo && fileInfo.url) {
            newFileMapping[fileId] = fileInfo.url;
          }
        });

        setFileMapping(prev => ({ ...prev, ...newFileMapping }));
      }

      // 处理未读消息
      const unreadMessages = processedMessages.filter(
        msg => msg.senderId !== currentUserId && msg.status === 'NEW',
      );

      if (unreadMessages.length > 0) {
        const res = await S.readOrderMessage(
          unreadMessages.map(msg => msg.messageId),
        );
        if (res.code === 'OK') {
          onMessagesChange(0);
        }
      }

      // 更新消息状态
      setMessages(prev => [...prev, ...processedMessages]);

      if (isLoading) {
        setIsLoading(false);
      }

      if (
        isFirstLoadRef.current ||
        needScrollToBottom ||
        contentRef.current.scrollHeight - contentRef.current.scrollTop < 1300
      ) {
        setTimeout(scrollToBottom, 100);
        setTimeout(scrollToBottom, 300);

        if (isFirstLoadRef.current) {
          isFirstLoadRef.current = false;
          // forceInitialScroll();
        }
        if (needScrollToBottom) {
          setNeedScrollToBottom(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      setIsLoading(false);
    }
  }, [
    code,
    isLoading,
    isFirstLoadRef,
    needScrollToBottom,
    scrollToBottom,
    // forceInitialScroll,
    fileMapping,
  ]);

  const pullDownRefresh = useCallback(async () => {
    try {
      const currentUserId = userIdRef.current;
      if (!currentUserId) {
        console.log('Waiting for userId to be available...');
        return;
      }

      // 记录当前内容高度和第一个元素，用于后续定位
      const scrollElement = contentRef.current;
      const oldScrollHeight = scrollElement?.scrollHeight || 0;
      const oldScrollTop = scrollElement?.scrollTop || 0;
      const oldFirstElement = scrollElement?.firstElementChild;

      const params: S.OrderMessageListParams = {
        orderId: code,
        sort: 'DESC',
        pageSize: 30,
      };

      const currentMessages = messagesRef.current;
      if (currentMessages.length > 0) {
        params.lastTimestamp = currentMessages[0].timestamp;
      }

      const res = await S.queryOrderMessages(params);

      if (res.data.length < params.pageSize) {
        setCanGetMoreMessages(false);
      }

      // 过滤出未处理过的新消息
      const newMessages = res.data
        .filter(msg => !processedMessageIds.current.has(msg.messageId))
        .reverse();

      if (newMessages.length === 0) {
        if (isLoading) setIsLoading(false);
        return;
      }

      // 处理新消息
      const processedMessages: S.Message[] = newMessages.map(msg => {
        // 将消息ID添加到已处理集合中
        processedMessageIds.current.add(msg.messageId);

        return {
          ...msg,
          displayTime: new Date(msg.timestamp).toLocaleString(),
          suffix:
            msg.messageType !== 'TEXT'
              ? extractFileSuffix(msg.message)
              : undefined,
        };
      });

      // 获取文件映射
      const fileIds = processedMessages
        .filter(msg => msg.messageType !== 'TEXT')
        .map(msg => msg.fileId);

      if (fileIds.length > 0) {
        const data = await S.getFilesByIds(fileIds);
        const newFileMapping = { ...fileMapping };

        Object.entries(data).forEach(([fileId, fileInfo]: [string, any]) => {
          if (fileInfo && fileInfo.url) {
            newFileMapping[fileId] = fileInfo.url;
          }
        });

        setFileMapping(prev => ({ ...prev, ...newFileMapping }));
      }

      // 处理未读消息
      const unreadMessages = processedMessages.filter(
        msg => msg.senderId !== currentUserId && msg.status === 'NEW',
      );

      if (unreadMessages.length > 0) {
        const res = await S.readOrderMessage(
          unreadMessages.map(msg => msg.messageId),
        );
        if (res.code === 'OK') {
          onMessagesChange(0);
        }
      }

      setMessages(prev => [...processedMessages, ...prev]);

      if (isLoading) {
        setIsLoading(false);
      }

      // 如果是首次加载或需要滚动到底部，才执行滚动
      if (isFirstLoadRef.current || needScrollToBottom) {
        setTimeout(scrollToBottom, 100);
        setTimeout(scrollToBottom, 300);

        if (isFirstLoadRef.current) {
          isFirstLoadRef.current = false;
        }
        if (needScrollToBottom) {
          setNeedScrollToBottom(false);
        }
      } else if (currentMessages.length > 0 && scrollElement) {
        // 维持滚动位置，让用户继续查看当前位置
        setTimeout(() => {
          if (scrollElement && oldFirstElement) {
            // 获取新的内容高度
            const newScrollHeight = scrollElement.scrollHeight;
            // 计算新增内容的高度
            const addedHeight = newScrollHeight - oldScrollHeight;
            // 设置滚动位置，保持用户当前查看的内容在视窗中
            scrollElement.scrollTop = oldScrollTop + addedHeight;
          }
        }, 10);
      }
    } catch (error) {
      console.error('Failed to fetch older messages:', error);
    }
  }, [code, isFirstLoadRef, scrollToBottom, needScrollToBottom, isLoading]);

  useEffect(() => {
    if (!apiCallTracker.hasInitialCallBeenMade) {
      apiCallTracker.hasInitialCallBeenMade = true;
      pullDownRefresh();
    }

    if (!fileStoreAuth) {
      loadFileStoreAuth();
    }

    pollingIntervalRef.current = setInterval(fetchMessages, 5000);

    const scrollTimeouts = [300, 600, 1000, 1500].map(delay =>
      setTimeout(() => {
        if (isFirstLoadRef.current || contentRef.current?.scrollHeight > 0) {
          scrollToBottom();
        }
      }, delay),
    );

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      scrollTimeouts.forEach(clearTimeout);
    };
  }, [
    pullDownRefresh,
    fetchMessages,
    scrollToBottom,
    fileStoreAuth,
    loadFileStoreAuth,
    isFirstLoadRef,
  ]);

  useEffect(() => {
    if (userId) {
      pullDownRefresh();
    }
  }, [userId, saleId, pullDownRefresh]);

  useEffect(() => {
    if (messages.length > 0 && isFirstLoadRef.current && contentRef.current) {
      if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(scrollToBottom);
        observer.observe(contentRef.current, {
          childList: true,
          subtree: true,
        });

        const timeout = setTimeout(() => {
          observer.disconnect();
        }, 5000);

        return () => {
          observer.disconnect();
          clearTimeout(timeout);
        };
      }
    }
  }, [messages, isFirstLoadRef, scrollToBottom]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        contentRef.current &&
        contentRef.current.scrollTop === 0 &&
        canGetMoreMessages
      ) {
        pullDownRefresh();
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (message.trim()) {
      const params: S.MessagePayload = {
        orderId: code,
        messageType: 'TEXT',
        message: message,
        receiverId: saleIdRef.current,
      };
      try {
        setMessage('');
        await S.sendOrderMessage(params);
        await fetchMessages();
        setNeedScrollToBottom(true);
      } catch (error: any) {
        antMessage.error(error.message);
      }
    }
  }, [code, message]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const handleChange = useCallback(
    async (info: UploadChangeParam) => {
      const { status, name, size } = info.file;

      if (!fileStoreAuth) {
        antMessage.error('File storage not available');
        return;
      }

      const storeKey = fileStoreAuth.getStoreKey(name);
      // 修改后缀提取方法，先获取原始文件名（不含文件大小部分）
      const suffix = extractFileSuffix(name) || '';
      const isImage = imgSuffix.includes(suffix.toLowerCase());

      if (status === 'uploading') {
        setUploading(true);
      } else if (status === 'done') {
        const fileUrl = URL.createObjectURL(info.file.originFileObj);

        Modal.confirm({
          title: 'Attach files cannot be withdrawn after being sent',
          closable: true,
          content: (
            <div>
              {isImage ? (
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Image
                    src={fileUrl}
                    alt={name}
                    style={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                    }}
                  />
                </div>
              ) : (
                <a
                  className={styles.attachmentName}
                  href={fileUrl}
                  download={name}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {name}
                </a>
              )}
            </div>
          ),
          okText: 'Send',
          cancelText: 'Cancel',
          onOk: async () => {
            const attr = new S.D3FormatAttr({
              name: name,
              size: size,
              uid: info.file.uid,
              type: suffix.toLowerCase() as S.D3SupportUploadFormatType,
            });

            try {
              const fileId = await S.createFile(
                fileStoreAuth.fileStore,
                storeKey,
                attr,
              );

              const params: S.MessagePayload = {
                orderId: code,
                messageType: 'FILE',
                fileId: fileId,
                message: fileNameAppendFormatFileSize(name, size),
                receiverId: saleIdRef.current,
              };

              await S.sendOrderMessage(params);

              await fetchMessages();

              antMessage.success(`${name} Upload Success`);
              setNeedScrollToBottom(true);
            } catch (error) {
              console.error('File upload error:', error);
              antMessage.error(`${name} processing failed`);
            } finally {
              setUploading(false);
              URL.revokeObjectURL(fileUrl);
            }
          },
          onCancel: () => {
            setUploading(false);
            URL.revokeObjectURL(fileUrl);
          },
        });
      } else if (status === 'error') {
        setUploading(false);
        antMessage.error(`${name} Upload Failed`);
      }
    },
    [code, fileStoreAuth],
  );

  /**
   * 将文件大小（字节）格式化为人类可读的形式
   * @param bytes 文件大小（字节）
   * @param decimals 小数点位数，默认为2
   * @returns 格式化后的文件大小字符串，如"2.5 MB"
   */
  function fileNameAppendFormatFileSize(
    fileName: string,
    bytes: number,
    decimals = 2,
  ): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    // 计算适合的单位级别
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // 转换到对应单位并保留指定小数位
    return (
      fileName +
      '(' +
      parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) +
      ' ' +
      sizes[i] +
      ')'
    );
  }

  const uploadProps = useMemo(
    () => ({
      action: fileStoreAuth?.url,
      data: { ...fileStoreAuth?.headers, storeAuth: fileStoreAuth },
      name: 'file',
      showUploadList: false,
      onChange: handleChange,
      disabled: uploading || isLoading,
      customRequest: (options: any) => {
        const { file } = options;
        const newFile = new File([file], file.name, {
          type: file.type,
        });

        // 传递修改后的文件和相关参数
        S.uploadRequest({
          ...options,
          store: fileStoreAuth,
          file: newFile,
        });
      },
    }),
    [fileStoreAuth, handleChange, uploading, isLoading],
  );

  // 下载文件
  const fileDownload = async (fileId: string) => {
    const data = await S.getFilesByIds([fileId]);
    const fileUrl = data[fileId].url;

    const link = document.createElement('a');
    link.href = fileUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const refreshImageUrl = async (fileId: string) => {
    try {
      const data = await S.getFilesByIds([fileId]);
      if (data && data[fileId] && data[fileId].url) {
        setFileMapping(prev => ({
          ...prev,
          [fileId]: data[fileId].url,
        }));
        return data[fileId].url;
      }
      return null;
    } catch (error) {
      console.error('Failed to refresh image URL:', error);
      return null;
    }
  };

  const handleImageError = async (fileId: string) => {
    await refreshImageUrl(fileId);
  };

  return (
    <>
      <div className={styles.content} ref={contentRef}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />}
              tip="Loading messages..."
            />
          </div>
        ) : messages.length === 0 ? (
          <div className={styles.emptyMessage}>
            <p>No messages yet. Start a conversation!</p>
          </div>
        ) : (
          messages.map(msg => (
            <div
              key={msg.messageId || msg.timestamp}
              className={`${styles.messageContainer} ${
                msg.senderId === userId
                  ? styles.customerMessage
                  : styles.salesMessage
              }`}
            >
              <div className={styles.messageRow}>
                {msg.senderId !== userId && (
                  <SalesAvatar className={styles.avatar} />
                )}
                <div className={styles.messageBox}>
                  <div className={styles.messageHeader}>
                    {msg.senderId !== userId ? (
                      <>
                        <span className={styles.senderRole}>Sales: </span>
                        <span className={styles.senderName}>{msg.sender}</span>
                        <span className={styles.messageTime}>
                          {msg.displayTime}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className={styles.senderName}>{msg.sender}</span>
                        <span className={styles.messageTime}>
                          {msg.displayTime}
                        </span>
                      </>
                    )}
                  </div>
                  <div className={styles.messageContent}>
                    {msg.messageType === 'TEXT' ? (
                      <p className={styles.messageText}>{msg.message}</p>
                    ) : msg.messageType === 'FILE' &&
                      imgSuffix.includes(msg.suffix || '') ? (
                      <Image
                        src={fileMapping[msg.fileId]}
                        alt={msg.message}
                        width={80}
                        preview={{
                          src: fileMapping[msg.fileId],
                          onVisibleChange: async visible => {
                            if (visible) {
                              const newUrl = await refreshImageUrl(msg.fileId);
                            }
                          },
                        }}
                        placeholder={
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: 80,
                              height: 80,
                              backgroundColor: '#f0f0f0',
                            }}
                          >
                            <Spin size="small" />
                          </div>
                        }
                        onError={() => handleImageError(msg.fileId)}
                      />
                    ) : (
                      <div className={styles.attachment}>
                        <span className={styles.attachmentIcon}>📎</span>
                        {/* 分离文件名和大小 */}
                        {(() => {
                          const { name, size } = separateFileNameAndSize(
                            msg.message,
                          );
                          return (
                            <div
                              className={styles.attachmentName}
                              onClick={() => {
                                fileDownload(msg.fileId);
                              }}
                            >
                              {name}
                              {size && (
                                <span
                                  className={styles.fileSize}
                                >{`(${size})`}</span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                {msg.senderId === userId && (
                  <UserAvatar className={styles.avatar} />
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.messageInputContainer}>
          <div className={styles.textareaWrapper}>
            <Input.TextArea
              className={styles.messageInput}
              placeholder="Type your message here..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={uploading || isLoading}
              autoSize={{ minRows: 1, maxRows: 6 }}
              style={{ resize: 'none' }}
            />
          </div>
          <Button
            disabled={uploading || isLoading}
            className={`${styles.sendButton} ${styles.sendButtonBg}`}
            onClick={() => handleSendMessage()}
          >
            <SenderIcon className={styles.uploadIcon} />
          </Button>
          <div>
            <Upload {...uploadProps}>
              <Button className={styles.sendButton} loading={uploading}>
                Send Files
              </Button>
            </Upload>
          </div>
        </div>
      </div>
    </>
  );
};

export default MessagePage;
